import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import './CategorySelector.css';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const API_BASE_URL = 'https://api.autocasses.com';

// Configurer l'élément racine pour l'accessibilité
Modal.setAppElement('#root'); // ou '#app-root' selon votre configuration

const CategorySelector = ({ 
  category, 
  onCategoryChange, 
  productDetails, 
  onDetailsChange,
  isEditMode = false,
  validationErrors = {}
}) => {
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [error, setError] = useState(null);
  
  // États pour les modales
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  
  // États pour la recherche
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [conditionSearch, setConditionSearch] = useState('');
  const [brandSearch, setBrandSearch] = useState('');

  // Marques de véhicules
  const vehicleBrands = useMemo(() => [
    { value: 'toyota', label: 'Toyota' },
    { value: 'honda', label: 'Honda' },
    { value: 'nissan', label: 'Nissan' },
    { value: 'mercedes', label: 'Mercedes-Benz' },
    { value: 'bmw', label: 'BMW' },
    { value: 'volkswagen', label: 'Volkswagen' },
    { value: 'peugeot', label: 'Peugeot' },
    { value: 'renault', label: 'Renault' },
    { value: 'hyundai', label: 'Hyundai' },
    { value: 'kia', label: 'Kia' },
    { value: 'ford', label: 'Ford' },
    { value: 'chevrolet', label: 'Chevrolet' },
    { value: 'audi', label: 'Audi' },
    { value: 'mazda', label: 'Mazda' },
    { value: 'suzuki', label: 'Suzuki' },
    { value: 'mitsubishi', label: 'Mitsubishi' },
    { value: 'subaru', label: 'Subaru' },
    { value: 'lexus', label: 'Lexus' },
    { value: 'jeep', label: 'Jeep' },
    { value: 'land_rover', label: 'Land Rover' }
  ], []);

  // Options de condition
  const conditionOptions = useMemo(() => [
    { value: 'Neuf', label: 'Neuf' },
    { value: 'Comme Neuf', label: 'Comme Neuf' },
    { value: 'Bon État', label: 'Bon État' },
    { value: 'État Correct', label: 'État Correct' },
    { value: 'Mauvais État', label: 'Mauvais État' },
    { value: 'Pour Pièces Uniquement', label: 'Pour Pièces Uniquement' }
  ], []);

  // Styles pour react-modal
  const customModalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      padding: '0',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      overflow: 'hidden'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000
    }
  };

  // Charger les catégories au démarrage
  useEffect(() => {
    fetchCategories();
  }, []);

  // Charger les sous-catégories quand une catégorie est sélectionnée
  useEffect(() => {
    if (category) {
      fetchSubcategories(category);
    } else {
      setSubcategories([]);
      onDetailsChange({ ...productDetails, sousCategorieId: '' });
    }
  }, [category]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/categories/all`);
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        const transformedCategories = data.map(cat => ({
          value: cat.id.toString(),
          label: cat.nom,
          description: cat.description || '',
          originalData: cat
        }));
        
        setMainCategories(transformedCategories);
      } else {
        throw new Error('Format de données invalide');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      
      let errorMessage = 'Impossible de charger les catégories';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Service catégories non disponible';
        } else if (error.response.status >= 500) {
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
        }
      } else if (error.request) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion.';
      }
      
      setError(errorMessage);
      
      // Catégories par défaut
      setMainCategories([
        { value: '1', label: 'Pièces Auto', description: 'Pièces détachées pour automobiles' },
        { value: '2', label: 'Pièces Moto', description: 'Pièces détachées pour motos' },
        { value: '3', label: 'Moteurs', description: 'Moteurs complets et pièces moteur' },
        { value: '4', label: 'Carrosserie', description: 'Pièces de carrosserie et tôlerie' },
        { value: '5', label: 'Véhicules Complets', description: 'Véhicules complets à vendre' },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      setLoadingSubcategories(true);
      
      const response = await fetch(
        `${API_BASE_URL}/api/souscat/by-category/${categoryId}`
      );
      const data = await response.json();
      
      if (data && data.success && Array.isArray(data.data)) {
        const transformedSubcategories = data.data.map(subCat => ({
          value: subCat.id.toString(),
          label: subCat.nom,
          description: subCat.description || '',
          originalData: subCat
        }));
        
        setSubcategories(transformedSubcategories);
        
        // Si l'utilisateur avait déjà sélectionné une sous-catégorie qui n'existe plus
        if (productDetails.sousCategorieId) {
          const subCatExists = data.data.some(sc => sc.id.toString() === productDetails.sousCategorieId);
          if (!subCatExists) {
            onDetailsChange({ ...productDetails, sousCategorieId: '' });
          }
        }
      } else {
        setSubcategories([]);
        onDetailsChange({ ...productDetails, sousCategorieId: '' });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sous-catégories:', error);
      setSubcategories([]);
      onDetailsChange({ ...productDetails, sousCategorieId: '' });
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleCategoryChange = (value) => {
    onCategoryChange(value);
    setShowCategoryModal(false);
    setCategorySearch('');
    
    if (!isEditMode) {
      // Réinitialiser seulement en mode création
      onDetailsChange({
        ...productDetails,
        sousCategorieId: '',
        compatibleModels: [],
        condition: '',
        brand: '',
        model: '',
        year: '',
        mileage: '',
        partNumber: '',
        warranty: ''
      });
    }
  };

  const handleSubCategoryChange = (value) => {
    onDetailsChange({ 
      ...productDetails, 
      sousCategorieId: value 
    });
    setShowSubcategoryModal(false);
    setSubcategorySearch('');
  };

  const handleConditionChange = (value) => {
    onDetailsChange({ 
      ...productDetails, 
      condition: value 
    });
    setShowConditionModal(false);
    setConditionSearch('');
  };

  const handleBrandChange = (value) => {
    onDetailsChange({ 
      ...productDetails, 
      brand: value 
    });
    setShowBrandModal(false);
    setBrandSearch('');
  };

  const handleCompatibleModelChange = (brand) => {
    const currentModels = productDetails?.compatibleModels || [];
    const isSelected = currentModels?.includes(brand);
    
    const updatedModels = isSelected
      ? currentModels?.filter(model => model !== brand)
      : [...currentModels, brand];
    
    onDetailsChange({
      ...productDetails,
      compatibleModels: updatedModels
    });
  };

  // Filtrer les catégories
  const filteredCategories = mainCategories.filter(cat =>
    cat.label.toLowerCase().includes(categorySearch.toLowerCase()) ||
    (cat.description && cat.description.toLowerCase().includes(categorySearch.toLowerCase()))
  );

  // Filtrer les sous-catégories
  const filteredSubcategories = subcategories.filter(sub =>
    sub.label.toLowerCase().includes(subcategorySearch.toLowerCase()) ||
    (sub.description && sub.description.toLowerCase().includes(subcategorySearch.toLowerCase()))
  );

  // Filtrer les conditions
  const filteredConditions = conditionOptions.filter(cond =>
    cond.label.toLowerCase().includes(conditionSearch.toLowerCase())
  );

  // Filtrer les marques
  const filteredBrands = vehicleBrands.filter(brand =>
    brand.label.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const isCompleteVehicle = () => {
    if (!category) return false;
    
    const selectedCategory = mainCategories.find(cat => cat.value === category);
    if (!selectedCategory) return false;
    
    const categoryName = selectedCategory.label.toLowerCase();
    return categoryName.includes('véhicule') || 
           categoryName.includes('vehicule') || 
           categoryName.includes('voiture') ||
           categoryName.includes('moto complète') ||
           categoryName.includes('camion') ||
           categoryName.includes('véhicules complets') ||
           categoryName.includes('véhicules hors d\'usage');
  };

  // Render Item pour la liste des catégories
  const renderCategoryItem = (item) => {
    const isSelected = category === item.value;
    return (
      <button
        key={item.value}
        className={`modal-item ${isSelected ? 'modal-item-selected' : ''}`}
        onClick={() => handleCategoryChange(item.value)}
        type="button"
      >
        <div className="item-icon-container">
          <Icon 
            name={getCategoryIcon(item.label)} 
            size={24} 
            color={isSelected ? "#3B82F6" : "#6B7280"} 
          />
        </div>
        <div className="item-content">
          <p className={`item-title ${isSelected ? 'item-title-selected' : ''}`}>
            {item.label}
          </p>
          {item.description && (
            <p className="item-description">
              {item.description}
            </p>
          )}
        </div>
        {isSelected && (
          <Icon name="CheckCircle" size={20} color="#3B82F6" />
        )}
      </button>
    );
  };

  // Render Item pour la liste des sous-catégories
  const renderSubcategoryItem = (item) => {
    const isSelected = productDetails?.sousCategorieId === item.value;
    return (
      <button
        key={item.value}
        className={`modal-item ${isSelected ? 'modal-item-selected' : ''}`}
        onClick={() => handleSubCategoryChange(item.value)}
        type="button"
      >
        <div className="item-icon-container">
          <Icon 
            name="Folder" 
            size={24} 
            color={isSelected ? "#3B82F6" : "#6B7280"} 
          />
        </div>
        <div className="item-content">
          <p className={`item-title ${isSelected ? 'item-title-selected' : ''}`}>
            {item.label}
          </p>
          {item.description && (
            <p className="item-description">
              {item.description}
            </p>
          )}
        </div>
        {isSelected && (
          <Icon name="CheckCircle" size={20} color="#3B82F6" />
        )}
      </button>
    );
  };

  // Render Item pour la liste des conditions
  const renderConditionItem = (item) => {
    const isSelected = productDetails?.condition === item.value;
    return (
      <button
        key={item.value}
        className={`modal-item ${isSelected ? 'modal-item-selected' : ''}`}
        onClick={() => handleConditionChange(item.value)}
        type="button"
      >
        <div className="item-icon-container">
          <Icon 
            name={getConditionIcon(item.value)} 
            size={24} 
            color={isSelected ? "#3B82F6" : "#6B7280"} 
          />
        </div>
        <div className="item-content">
          <p className={`item-title ${isSelected ? 'item-title-selected' : ''}`}>
            {item.label}
          </p>
        </div>
        {isSelected && (
          <Icon name="CheckCircle" size={20} color="#3B82F6" />
        )}
      </button>
    );
  };

  // Render Item pour la liste des marques
  const renderBrandItem = (item) => {
    const isSelected = productDetails?.brand === item.value;
    return (
      <button
        key={item.value}
        className={`modal-item ${isSelected ? 'modal-item-selected' : ''}`}
        onClick={() => handleBrandChange(item.value)}
        type="button"
      >
        <div className="item-icon-container">
          <Icon 
            name="Car" 
            size={24} 
            color={isSelected ? "#3B82F6" : "#6B7280"} 
          />
        </div>
        <div className="item-content">
          <p className={`item-title ${isSelected ? 'item-title-selected' : ''}`}>
            {item.label}
          </p>
        </div>
        {isSelected && (
          <Icon name="CheckCircle" size={20} color="#3B82F6" />
        )}
      </button>
    );
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('auto') || name.includes('voiture')) return 'Car';
    if (name.includes('moto')) return 'Motorcycle';
    if (name.includes('moteur')) return 'Engine';
    if (name.includes('carrosserie')) return 'Car';
    if (name.includes('véhicule') || name.includes('vehicule')) return 'Car';
    if (name.includes('pneu')) return 'Circle';
    if (name.includes('frein')) return 'Brakes';
    if (name.includes('électrique')) return 'Zap';
    if (name.includes('batterie')) return 'Battery';
    return 'Package';
  };

  const getConditionIcon = (condition) => {
    switch (condition) {
      case 'Neuf': return 'Star';
      case 'Comme Neuf': return 'Star';
      case 'Bon État': return 'Check';
      case 'État Correct': return 'Info';
      case 'Mauvais État': return 'AlertTriangle';
      case 'Pour Pièces Uniquement': return 'Wrench';
      default: return 'HelpCircle';
    }
  };

  const getSelectedCategoryLabel = () => {
    const selected = mainCategories.find(cat => cat.value === category);
    return selected ? selected.label : 'Sélectionner une catégorie';
  };

  const getSelectedSubcategoryLabel = () => {
    const selected = subcategories.find(sub => sub.value === productDetails?.sousCategorieId);
    return selected ? selected.label : 'Sélectionner une sous-catégorie';
  };

  const getSelectedConditionLabel = () => {
    const selected = conditionOptions.find(cond => cond.value === productDetails?.condition);
    return selected ? selected.label : 'Sélectionner l\'état';
  };

  const getSelectedBrandLabel = () => {
    const selected = vehicleBrands.find(brand => brand.value === productDetails?.brand);
    return selected ? selected.label : 'Sélectionner une marque';
  };

  // Fonction pour fermer toutes les modales
  const closeAllModals = () => {
    setShowCategoryModal(false);
    setShowSubcategoryModal(false);
    setShowConditionModal(false);
    setShowBrandModal(false);
    
    // Réinitialiser les recherches
    setCategorySearch('');
    setSubcategorySearch('');
    setConditionSearch('');
    setBrandSearch('');
  };

  return (
    <div className="category-selector-container">
      {isEditMode && (
        <div className="edit-mode-notice">
          <Icon name="Edit" size={16} color="#007AFF" />
          <p className="edit-mode-notice-text">
            Note: La catégorie principale ne peut pas être modifiée en mode édition.
          </p>
        </div>
      )}

      {/* Catégorie Principale */}
      <div className="section">
        <label className="label">Catégorie du Produit *</label>
        <p className="helper-text">
          {isEditMode ? 'Catégorie actuelle (non modifiable)' : 'Choisissez la catégorie principale de votre annonce'}
        </p>
        
        <button
          className={`selector-button ${!category ? 'selector-button-empty' : ''} ${validationErrors.category ? 'selector-button-error' : ''}`}
          onClick={() => setShowCategoryModal(true)}
          disabled={isEditMode}
          type="button"
        >
          <div className="selector-left">
            <Icon 
              name="Tag" 
              size={22} 
              color={category ? "#3B82F6" : "#666"} 
            />
            <div className="selector-content">
              <p className="selector-value">
                {getSelectedCategoryLabel()}
              </p>
              {category && (
                <p className="selector-description">
                  {mainCategories.find(cat => cat.value === category)?.description || ''}
                </p>
              )}
            </div>
          </div>
          <Icon name="ChevronRight" size={22} color="#9CA3AF" />
        </button>
        {validationErrors.category && (
          <p className="error-message">{validationErrors.category}</p>
        )}
      </div>

      {/* Sous-catégorie */}
      {category && (
        <div className="section">
          <label className="label">Sous-catégorie</label>
          <p className="helper-text">
            Précisez le type exact de produit (optionnel)
          </p>
          
          <button
            className={`selector-button ${!productDetails?.sousCategorieId ? 'selector-button-empty' : ''}`}
            onClick={() => setShowSubcategoryModal(true)}
            type="button"
          >
            <div className="selector-left">
              <Icon 
                name="Folder" 
                size={22} 
                color={productDetails?.sousCategorieId ? "#3B82F6" : "#666"} 
              />
              <div className="selector-content">
                <p className="selector-value">
                  {getSelectedSubcategoryLabel()}
                </p>
                {productDetails?.sousCategorieId && (
                  <p className="selector-description">
                    {subcategories.find(sub => sub.value === productDetails.sousCategorieId)?.description || ''}
                  </p>
                )}
              </div>
            </div>
            <Icon name="ChevronRight" size={22} color="#9CA3AF" />
          </button>
        </div>
      )}

      {/* Modèles Compatibles */}
      {category && !isCompleteVehicle() && (
        <div className="section">
          <label className="label">Marques Compatibles (Optionnel)</label>
          <p className="helper-text">
            Sélectionnez les marques de véhicules compatibles
          </p>
          <div className="checkbox-grid">
            {vehicleBrands.map((brand) => (
              <div key={brand.value} className="checkbox-item">
                <button
                  className={`checkbox-touchable ${(productDetails?.compatibleModels || []).includes(brand.value) ? 'checkbox-selected' : ''}`}
                  onClick={() => handleCompatibleModelChange(brand.value)}
                  type="button"
                >
                  <Icon 
                    name={(productDetails?.compatibleModels || []).includes(brand.value) ? "CheckSquare" : "Square"} 
                    size={24} 
                    color={(productDetails?.compatibleModels || []).includes(brand.value) ? "#3B82F6" : "#666"} 
                  />
                  <span className={`checkbox-label ${(productDetails?.compatibleModels || []).includes(brand.value) ? 'checkbox-label-selected' : ''}`}>
                    {brand.label}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Détails du Véhicule pour Véhicules Complets */}
      {category && isCompleteVehicle() && (
        <div className="section">
          <label className="label">Détails du Véhicule *</label>
          <div className="details-grid">
            <div className="grid-item">
              <label className="label">Marque du Véhicule *</label>
              <button
                className={`selector-button ${!productDetails?.brand ? 'selector-button-empty' : ''} ${validationErrors.brand ? 'selector-button-error' : ''}`}
                onClick={() => setShowBrandModal(true)}
                type="button"
              >
                <div className="selector-left">
                  <Icon 
                    name="Car" 
                    size={20} 
                    color={productDetails?.brand ? "#3B82F6" : "#666"} 
                  />
                  <p className="selector-value">
                    {getSelectedBrandLabel()}
                  </p>
                </div>
                <Icon name="ChevronDown" size={20} color="#9CA3AF" />
              </button>
              {validationErrors.brand && (
                <p className="error-message">{validationErrors.brand}</p>
              )}
            </div>

            <div className="grid-item">
              <label className="label">Modèle *</label>
              <Input
                type="text"
                placeholder="ex: Camry, Civic, C3"
                value={productDetails?.model || ''}
                onChange={(e) => onDetailsChange({ ...productDetails, model: e.target.value })}
                fullWidth
                error={validationErrors.model}
              />
            </div>

            <div className="grid-item">
              <label className="label">Année *</label>
              <Input
                type="number"
                placeholder="ex: 2015"
                value={productDetails?.year?.toString() || ''}
                onChange={(e) => onDetailsChange({ ...productDetails, year: e.target.value })}
                maxLength={4}
                fullWidth
                error={validationErrors.year}
              />
            </div>

            <div className="grid-item">
              <label className="label">Kilométrage (km) (Optionnel)</label>
              <Input
                type="number"
                placeholder="ex: 150000"
                value={productDetails?.mileage?.toString() || ''}
                onChange={(e) => onDetailsChange({ ...productDetails, mileage: e.target.value })}
                fullWidth
              />
            </div>
          </div>
        </div>
      )}

      {/* État */}
      <div className="section">
        <label className="label">État du Produit *</label>
        <p className="helper-text">
          Décrivez honnêtement l'état de votre article
        </p>
        
        <button
          className={`selector-button ${!productDetails?.condition ? 'selector-button-empty' : ''} ${validationErrors.condition ? 'selector-button-error' : ''}`}
          onClick={() => setShowConditionModal(true)}
          type="button"
        >
          <div className="selector-left">
            <Icon 
              name={getConditionIcon(productDetails?.condition) || "HelpCircle"} 
              size={22} 
              color={productDetails?.condition ? "#3B82F6" : "#666"} 
            />
            <p className="selector-value">
              {getSelectedConditionLabel()}
            </p>
          </div>
          <Icon name="ChevronRight" size={22} color="#9CA3AF" />
        </button>
        {validationErrors.condition && (
          <p className="error-message">{validationErrors.condition}</p>
        )}
      </div>

      {/* Détails Supplémentaires */}
      <div className="section">
        <label className="label">Détails Supplémentaires (Optionnel)</label>
        <div className="details-grid">
          <div className="grid-item">
            <label className="label">Numéro de Pièce/Référence</label>
            <p className="helper-text">
              Numéro OEM ou référence constructeur
            </p>
            <Input
              type="text"
              placeholder="ex: 12345-67890, OEM-REF-001"
              value={productDetails?.partNumber || ''}
              onChange={(e) => onDetailsChange({ ...productDetails, partNumber: e.target.value })}
              fullWidth
            />
          </div>

          <div className="grid-item">
            <label className="label">Garantie</label>
            <p className="helper-text">
              Durée de garantie offerte
            </p>
            <Input
              type="text"
              placeholder="ex: 6 mois, 1 an, Aucune"
              value={productDetails?.warranty || ''}
              onChange={(e) => onDetailsChange({ ...productDetails, warranty: e.target.value })}
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* Modal pour la sélection de la catégorie avec react-modal */}
      <Modal
        isOpen={showCategoryModal}
        onRequestClose={() => setShowCategoryModal(false)}
        style={customModalStyles}
        contentLabel="Sélectionnez une catégorie"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="modal-container">
          <div className="modal-header">
            <div className="modal-title-container">
              <button
                className="modal-back-button"
                onClick={() => setShowCategoryModal(false)}
                type="button"
              >
                <Icon name="ArrowLeft" size={24} color="#111827" />
              </button>
              <div>
                <h2 className="modal-title">Sélectionnez une catégorie</h2>
                <p className="modal-subtitle">
                  {filteredCategories.length} catégorie(s) disponible(s)
                </p>
              </div>
            </div>
          </div>

          <div className="search-container">
            <Icon name="Search" size={22} color="#6B7280" className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher une catégorie..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              autoFocus
            />
            {categorySearch.length > 0 && (
              <button
                className="clear-search-button"
                onClick={() => setCategorySearch('')}
                type="button"
              >
                <Icon name="XCircle" size={20} color="#9CA3AF" />
              </button>
            )}
          </div>

          {loadingCategories ? (
            <div className="modal-loading">
              <div className="spinner"></div>
              <p className="modal-loading-text">Chargement des catégories...</p>
            </div>
          ) : error ? (
            <div className="modal-error">
              <Icon name="AlertCircle" size={50} color="#DC2626" />
              <p className="modal-error-text">{error}</p>
              <button
                className="modal-retry-button"
                onClick={fetchCategories}
                type="button"
              >
                <span className="modal-retry-text">⟳ Réessayer</span>
              </button>
            </div>
          ) : (
            <div className="modal-list-content">
              {filteredCategories.length > 0 ? (
                filteredCategories.map(renderCategoryItem)
              ) : (
                <div className="modal-empty">
                  <Icon name="Tag" size={60} color="#D1D5DB" />
                  <p className="modal-empty-text">Aucune catégorie trouvée</p>
                  <p className="modal-empty-subtext">
                    Essayez avec un autre terme de recherche
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal pour la sélection de la sous-catégorie */}
      <Modal
        isOpen={showSubcategoryModal}
        onRequestClose={() => setShowSubcategoryModal(false)}
        style={customModalStyles}
        contentLabel="Sélectionnez une sous-catégorie"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="modal-container">
          <div className="modal-header">
            <div className="modal-title-container">
              <button
                className="modal-back-button"
                onClick={() => setShowSubcategoryModal(false)}
                type="button"
              >
                <Icon name="ArrowLeft" size={24} color="#111827" />
              </button>
              <div>
                <h2 className="modal-title">Sélectionnez une sous-catégorie</h2>
                <p className="modal-subtitle">
                  {filteredSubcategories.length} sous-catégorie(s) disponible(s)
                </p>
              </div>
            </div>
          </div>

          <div className="search-container">
            <Icon name="Search" size={22} color="#6B7280" className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher une sous-catégorie..."
              value={subcategorySearch}
              onChange={(e) => setSubcategorySearch(e.target.value)}
              autoFocus
            />
            {subcategorySearch.length > 0 && (
              <button
                className="clear-search-button"
                onClick={() => setSubcategorySearch('')}
                type="button"
              >
                <Icon name="XCircle" size={20} color="#9CA3AF" />
              </button>
            )}
          </div>

          {loadingSubcategories ? (
            <div className="modal-loading">
              <div className="spinner"></div>
              <p className="modal-loading-text">Chargement des sous-catégories...</p>
            </div>
          ) : (
            <div className="modal-list-content">
              {filteredSubcategories.length > 0 ? (
                filteredSubcategories.map(renderSubcategoryItem)
              ) : (
                <div className="modal-empty">
                  <Icon name="Folder" size={60} color="#D1D5DB" />
                  <p className="modal-empty-text">Aucune sous-catégorie disponible</p>
                  <p className="modal-empty-subtext">
                    {subcategorySearch ? 'Essayez avec un autre terme' : 'Aucune sous-catégorie pour cette catégorie'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal pour la sélection de l'état */}
      <Modal
        isOpen={showConditionModal}
        onRequestClose={() => setShowConditionModal(false)}
        style={customModalStyles}
        contentLabel="Sélectionnez l'état du produit"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="modal-container">
          <div className="modal-header">
            <div className="modal-title-container">
              <button
                className="modal-back-button"
                onClick={() => setShowConditionModal(false)}
                type="button"
              >
                <Icon name="ArrowLeft" size={24} color="#111827" />
              </button>
              <div>
                <h2 className="modal-title">Sélectionnez l'état du produit</h2>
                <p className="modal-subtitle">
                  {filteredConditions.length} état(s) disponible(s)
                </p>
              </div>
            </div>
          </div>

          <div className="search-container">
            <Icon name="Search" size={22} color="#6B7280" className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un état..."
              value={conditionSearch}
              onChange={(e) => setConditionSearch(e.target.value)}
              autoFocus
            />
            {conditionSearch.length > 0 && (
              <button
                className="clear-search-button"
                onClick={() => setConditionSearch('')}
                type="button"
              >
                <Icon name="XCircle" size={20} color="#9CA3AF" />
              </button>
            )}
          </div>

          <div className="modal-list-content">
            {filteredConditions.map(renderConditionItem)}
          </div>
        </div>
      </Modal>

      {/* Modal pour la sélection de la marque */}
      <Modal
        isOpen={showBrandModal}
        onRequestClose={() => setShowBrandModal(false)}
        style={customModalStyles}
        contentLabel="Sélectionnez une marque"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="modal-container">
          <div className="modal-header">
            <div className="modal-title-container">
              <button
                className="modal-back-button"
                onClick={() => setShowBrandModal(false)}
                type="button"
              >
                <Icon name="ArrowLeft" size={24} color="#111827" />
              </button>
              <div>
                <h2 className="modal-title">Sélectionnez une marque</h2>
                <p className="modal-subtitle">
                  {filteredBrands.length} marque(s) disponible(s)
                </p>
              </div>
            </div>
          </div>

          <div className="search-container">
            <Icon name="Search" size={22} color="#6B7280" className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher une marque..."
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              autoFocus
            />
            {brandSearch.length > 0 && (
              <button
                className="clear-search-button"
                onClick={() => setBrandSearch('')}
                type="button"
              >
                <Icon name="XCircle" size={20} color="#9CA3AF" />
              </button>
            )}
          </div>

          <div className="modal-list-content">
            {filteredBrands.map(renderBrandItem)}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CategorySelector;